import { Component, ElementType, HTMLProps } from 'react';

import { ExpandableBoxElement } from './style';

export enum Direction {
    Horizontal = 'Horizontal',
    Vertical = 'Vertical',
}

const SCROLL_PROPERTY: { [key in Direction]: ScrollProperty } = {
    [Direction.Horizontal]: 'scrollWidth',
    [Direction.Vertical]: 'scrollHeight',
};

const SCROLL_SIZE: { [key in Direction]: ScrollSize } = {
    [Direction.Horizontal]: 'width',
    [Direction.Vertical]: 'height',
};

export interface ExpandableBoxProps {
    expanded: boolean;
    onCollapse?: () => void;
    onExpanse?: () => void;
    collapsedSize: number;
    direction: Direction;
    renderZeroSize?: boolean;
    as?: ElementType;
    role?: HTMLProps<HTMLDivElement>['role'];
}

type ScrollProperty = 'scrollWidth' | 'scrollHeight';
type ScrollSize = 'width' | 'height';

export default class ExpandableBox extends Component<React.PropsWithChildren<ExpandableBoxProps>> {
    static defaultProps = {
        collapsedSize: 0,
        direction: Direction.Vertical,
    };

    private expanding = false;
    private collapsing = false;
    private mounted = false;

    private collapseStartFrameHandle?: number;
    private collapseEndFrameHandle?: number;

    private width?: number;
    private height?: number;

    private transition?: string;

    private readonly scrollProperty: ScrollProperty;
    private readonly sizeProperty: ScrollSize;

    private element?: HTMLElement | null;

    constructor(props: ExpandableBoxProps) {
        super(props);

        this.scrollProperty = SCROLL_PROPERTY[props.direction];
        this.sizeProperty = SCROLL_SIZE[props.direction];
    }

    componentDidMount() {
        if (this.props.expanded) {
            this.mounted = true;
            this.expandTransitionHandler();
        }
    }

    componentDidUpdate(prevProps: ExpandableBoxProps) {
        const { expanded } = this.props;

        if (expanded && !prevProps.expanded) {
            if (this.collapsing) {
                this.cancelCollapse();
            }
            this.expand();
        }
        if (!expanded && prevProps.expanded) {
            if (this.expanding) {
                this.cancelExpand();
            }
            this.collapse();
        }
    }

    componentWillUnmount() {
        if (this.expanding) {
            this.cancelExpand();
        }

        if (this.collapsing) {
            this.cancelCollapse();
        }
    }

    expand = () => {
        if (!this.element) {
            return;
        }

        this.expanding = true;
        this.mounted = true;
        this.element.addEventListener('transitionend', this.expandTransitionHandler);
        const scrollProperty = this.element[this.scrollProperty];
        this.element.style[this.sizeProperty] = `${scrollProperty}px`;
    };

    expandTransitionHandler = () => {
        this.expanding = false;

        if (this.element) {
            this.element.removeEventListener('transitionend', this.expandTransitionHandler);
            this.element.style[this.sizeProperty] = 'auto';

            if (this.props.onExpanse) {
                this.props.onExpanse();
            }
        }
    };

    cancelExpand = () => {
        if (this.element) {
            this.element.removeEventListener('transitionend', this.expandTransitionHandler);
        }

        this.expanding = false;
    };

    collapse = () => {
        if (!this.element) {
            return;
        }

        this.collapsing = true;
        const { transition } = this.element.style;
        this[this.sizeProperty] = this.element[this.scrollProperty];
        this.transition = transition || '';
        this.element.style.transition = '';

        this.collapseStartFrameHandle = window.requestAnimationFrame(this.collapseStartFrame);
    };

    collapseStartFrame = () => {
        this.collapseStartFrameHandle = undefined;

        if (this.element) {
            this.element.style[this.sizeProperty] = `${this[this.sizeProperty]}px`;
            this.element.style.transition = this.transition || '';
            this[this.sizeProperty] = undefined;
            this.transition = undefined;

            this.collapseEndFrameHandle = window.requestAnimationFrame(this.collapseEndFrame);
        }
    };

    collapseEndFrame = () => {
        this.collapseEndFrameHandle = undefined;

        if (this.element) {
            this.element.addEventListener('transitionend', this.collapseTransitionHandler);
            this.element.style[this.sizeProperty] = `${this.props.collapsedSize}px`;
        }
    };

    collapseTransitionHandler = () => {
        this.collapsing = false;

        if (this.element) {
            this.element.removeEventListener('transitionend', this.collapseTransitionHandler);

            if (this.props.onCollapse) {
                this.props.onCollapse();
            }
        }
    };

    cancelCollapse = () => {
        if (this.collapseStartFrameHandle) {
            window.cancelAnimationFrame(this.collapseStartFrameHandle);
            this.collapseStartFrameHandle = undefined;
        }

        if (this.collapseEndFrameHandle) {
            window.cancelAnimationFrame(this.collapseEndFrameHandle);
            this.collapseEndFrameHandle = undefined;
        }

        if (this.element) {
            this.element.removeEventListener('transitionend', this.collapseTransitionHandler);
        }

        this.collapsing = false;
    };

    refHandler = (c: HTMLElement | null) => (this.element = c);

    render() {
        const { collapsedSize, expanded, renderZeroSize, direction, ...other } = this.props;

        if (!this.mounted && !expanded && collapsedSize === 0 && !renderZeroSize) {
            return null;
        }

        return (
            <ExpandableBoxElement
                ref={this.refHandler}
                collapsedSize={collapsedSize}
                expanded={expanded}
                renderZeroSize={renderZeroSize}
                $direction={direction}
                {...other}
            />
        );
    }
}
